import { useEffect, useState, useCallback } from 'react';
import { View, ScrollView, Platform, useWindowDimensions } from 'react-native';
import { Appbar, Card, TextInput, Chip, Button, HelperText, Surface, ActivityIndicator, Text, useTheme } from 'react-native-paper';
import { Sidebar, SIDEBAR_WIDTH } from '../../../src/components/Sidebar';
import Logo from '../../../src/components/Logo';
import { useUser } from '../../../src/hooks/useUser';
import { useProfile } from '../../../src/hooks/useProfile';
import { DriverAssignmentModal } from '../../../src/components/DriverAssignmentModal';
import { TransferModal } from '../../../src/components/TransferModal';
import FuelTypeSelector from '../../../src/components/FuelTypeSelector';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useFocusEffect } from '@react-navigation/native';
import apiService, { updateCompanyVehicle, transferVehicle } from '../../../src/api/apiService';
import { API_ENDPOINTS } from '../../../src/api/config';
import { useCompanyContext } from '../../../src/context/CompanyContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export default function VehicleDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { t } = useTranslation('manageVehicles');
  const { width, height } = useWindowDimensions();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const isLargeScreen = width > 1024;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { handleLogout, userData } = useUser();
  const { hasOwner, hasDriver, selectedProfile, updateProfile } = useProfile(userData?.groups || []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vehicle, setVehicle] = useState<any>(null);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [allDrivers, setAllDrivers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const { selectedCompanyId } = useCompanyContext();

  const load = async () => {
    setLoading(true);
    try {
      // Fetch vehicle details
      const vehResp: any = await apiService.get(`${API_ENDPOINTS.VEHICLES}${id}/`);
      const veh = vehResp.data;
      setVehicle(veh);
      
      // Fetch all drivers (with a large page size to get all at once)
      let allDriversData: any[] = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const drvResp: any = await apiService.get(`${API_ENDPOINTS.COMPANY_DRIVERS}?page=${page}&page_size=100`);
        const responseData = drvResp.data;
        const pageData = responseData?.results || responseData || [];
        allDriversData = [...allDriversData, ...pageData];
        
        // Check if there are more pages
        hasMore = responseData?.next === true && pageData.length > 0;
        page++;
      }
      
      // Map assigned drivers from vehicle (users) to employment records
      const assignedDrivers = (veh.drivers || []).map((user: any) => {
        // Find the employment record that matches this user
        const employment = allDriversData.find((emp: any) => 
          emp.user_id === user.id || 
          emp.user_email?.toLowerCase() === user.email?.toLowerCase()
        );
        return {
          id: employment?.id || user.id, // Use employment ID for assignment operations
          name: user.full_name || user.name || user.email || employment?.user_full_name,
          email: user.email || employment?.user_email,
        };
      });
      setDrivers(assignedDrivers);
      
      // Set all drivers for the modal (all employment records)
      setAllDrivers(allDriversData.map((emp: any) => ({
        id: emp.id,
        name: emp.user_full_name,
        email: emp.user_email,
      })));
    } catch (e: any) {
      setError(e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      load();
    }, [id])
  );

  const save = async () => {
    if (!vehicle) return;
    setSaving(true);
    setError(null);
    try {
      const response = await updateCompanyVehicle(Number(id), {
        registration_number: vehicle.registration_number,
        make: vehicle.make || undefined,
        model: vehicle.model || undefined,
        year: vehicle.year || undefined,
        fuel_type: vehicle.fuel_type || undefined,
      });
      if (response.ok) {
        router.back();
      } else {
        const errorData = response.data as any;
        setError(errorData?.detail || errorData?.message || 'Error updating vehicle');
      }
    } catch (e: any) {
      setError(e?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const assignedEmploymentIds = drivers.map(d => d.id);

  const toggleAssign = async (employmentId: number) => {
    try {
      if (assignedEmploymentIds.includes(employmentId)) {
        await apiService.post(`${API_ENDPOINTS.VEHICLES}${id}/unassign/`, { employment_id: employmentId });
        setDrivers(d => d.filter((x: any) => x.id !== employmentId));
      } else {
        await apiService.post(`${API_ENDPOINTS.VEHICLES}${id}/assign/`, { employment_id: employmentId });
        const emp = allDrivers.find(d => d.id === employmentId);
        if (emp) {
          setDrivers(d => [...d, emp]);
        }
      }
      // Refresh to ensure consistency
      await load();
    } catch (e: any) {
      setError(e?.message || 'Error');
    }
  };

  const handleBulkAssign = async (employmentIds: number[]) => {
    try {
      // Assign each driver sequentially
      for (const employmentId of employmentIds) {
        await apiService.post(`${API_ENDPOINTS.VEHICLES}${id}/assign/`, { employment_id: employmentId });
        const emp = allDrivers.find(d => d.id === employmentId);
        if (emp && !drivers.some(d => d.id === employmentId)) {
          setDrivers(d => [...d, emp]);
        }
      }
      // Refresh data to ensure consistency
      await load();
    } catch (e: any) {
      setError(e?.message || 'Error assigning drivers');
      throw e; // Re-throw to let modal handle it
    }
  };

  const handleTransfer = async (targetCompanyId: number) => {
    const response = await transferVehicle({
      vehicle_id: Number(id),
      target_company_id: targetCompanyId,
    });
    
    if (!response.ok) {
      const errorData = response.data as any;
      throw new Error(errorData?.error || errorData?.detail || 'Error transferring vehicle');
    }
    
    // Refresh data and navigate back
    await load();
    router.back();
  };

  // Calcular height total do header incluindo status bar
  const headerHeight = 48; // Altura compacta para mobile
  const topPadding = Platform.OS === 'ios' ? insets.top : Platform.OS === 'android' ? insets.top : 0;
  const totalHeaderHeight = isLargeScreen ? 0 : headerHeight + topPadding;

  const renderHeader = () => {
    if (isLargeScreen) return null;
    return (
      <Appbar.Header style={{ 
        height: headerHeight + topPadding, 
        backgroundColor: theme.colors.surface, 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1,
        alignItems: 'center',
        paddingTop: topPadding,
      }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} color={theme.colors.onSurface} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo size={Platform.OS === 'web' ? 'medium' : 'mobile'} />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      {isLargeScreen && (
        <Sidebar isOpen={true} onClose={() => {}} screenWidth={width} onLogout={handleLogout} isOwner={hasOwner} isDriver={hasDriver} selectedProfile={selectedProfile} onSwitchProfile={hasDriver && hasOwner ? updateProfile : undefined} />
      )}
      <Surface style={{ flex: 1, ...(isLargeScreen ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        {renderHeader()}
        <Appbar.Header style={isLargeScreen ? {} : { display: 'none' }}>
          <Appbar.BackAction onPress={() => router.back()} />
          <Appbar.Content title={t('vehicleDetails','Vehicle Details')} />
          <Appbar.Action icon="swap-horizontal" onPress={() => setTransferModalVisible(true)} />
          <Appbar.Action icon="content-save" onPress={save} disabled={saving} />
        </Appbar.Header>
        <ScrollView style={{ flex: 1, padding: 12, ...(isLargeScreen ? {} : { marginTop: totalHeaderHeight }) }}>
          {vehicle && (
            <Card style={{ marginBottom: 12 }}>
              <Card.Title title={t('vehicleInformation', 'Vehicle Information')} />
              <Card.Content>
                <TextInput 
                  label={t('registration','Registration')} 
                  value={vehicle.registration_number || ''} 
                  onChangeText={(v)=>setVehicle((x:any)=>({...x,registration_number:v}))} 
                  style={{ marginBottom: 8 }} 
                  disabled={saving}
                  mode="outlined"
                  autoCapitalize="characters"
                />
                <TextInput 
                  label={t('make','Make')} 
                  value={vehicle.make || ''} 
                  onChangeText={(v)=>setVehicle((x:any)=>({...x,make:v}))} 
                  style={{ marginBottom: 8 }} 
                  disabled={saving}
                  mode="outlined"
                />
                <TextInput 
                  label={t('model','Model')} 
                  value={vehicle.model || ''} 
                  onChangeText={(v)=>setVehicle((x:any)=>({...x,model:v}))} 
                  style={{ marginBottom: 8 }} 
                  disabled={saving}
                  mode="outlined"
                />
                <TextInput 
                  label={t('year','Year')} 
                  value={String(vehicle.year||'')} 
                  onChangeText={(v)=>setVehicle((x:any)=>({...x,year: Number(v)||undefined}))} 
                  keyboardType="numeric" 
                  style={{ marginBottom: 8 }}
                  disabled={saving}
                  mode="outlined"
                />
                {vehicle.fuel_type && (
                  <View style={{ marginBottom: 8 }}>
                    <FuelTypeSelector
                      value={vehicle.fuel_type}
                      onChange={(value) => {
                        setVehicle((x: any) => ({ ...x, fuel_type: value || undefined }));
                      }}
                    />
                  </View>
                )}
                {!vehicle.fuel_type && (
                  <View style={{ marginBottom: 8 }}>
                    <Button
                      mode="outlined"
                      icon="plus"
                      onPress={() => setVehicle((x: any) => ({ ...x, fuel_type: 'gasolina' }))}
                      disabled={saving}
                    >
                      {t('add', 'Add')} {t('fuelType', 'Fuel Type')}
                    </Button>
                  </View>
                )}
                {error && <HelperText type="error">{error}</HelperText>}
              </Card.Content>
            </Card>
          )}

          <Card>
            <Card.Title 
              title={t('assignedDrivers','Assigned drivers')}
              right={() => (
                <Button
                  mode="outlined"
                  icon="plus"
                  onPress={() => setAssignmentModalVisible(true)}
                  compact
                >
                  {t('assign', 'Assign')}
                </Button>
              )}
            />
            <Card.Content>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {drivers.map(d => (
                  <Chip
                    key={d.id}
                    icon="account"
                    mode="flat"
                    onClose={() => toggleAssign(d.id)}
                  >
                    {d.name}
                  </Chip>
                ))}
                {drivers.length === 0 && (
                  <Text style={{ color: '#666', fontStyle: 'italic' }}>
                    {t('noAssignedDrivers', 'No drivers assigned')}
                  </Text>
                )}
              </View>
            </Card.Content>
          </Card>

          <DriverAssignmentModal
            visible={assignmentModalVisible}
            onDismiss={() => setAssignmentModalVisible(false)}
            onAssign={handleBulkAssign}
            assignedEmploymentIds={assignedEmploymentIds}
          />

          <TransferModal
            visible={transferModalVisible}
            onDismiss={() => setTransferModalVisible(false)}
            onTransfer={handleTransfer}
            title={t('transfer.vehicleTitle', 'Transfer Vehicle')}
            description={t('transfer.vehicleDescription', 'Select the company to transfer this vehicle to. All driver assignments will be removed.')}
            currentCompanyId={selectedCompanyId || undefined}
          />
        </ScrollView>
        {!isLargeScreen && (
          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} screenWidth={width} onLogout={handleLogout} isOwner={hasOwner} isDriver={hasDriver} selectedProfile={selectedProfile} onSwitchProfile={hasDriver && hasOwner ? updateProfile : undefined} />
        )}
      </Surface>
    </View>
  );
}
